import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { AsyncEntity } from '../../global';
import createAsyncThunkFromFetch from '../../store/middlewares/createAsyncThunkFromFetch';
import {
  createPackage as createPackageAPI,
  deletePackage as deletePackageAPI,
  getPackage,
  getPackages,
  postPackageActivate,
  updatePackage as updatePackageAPI,
} from './companyPackages.api';
import { CompanyPackage } from './companyPackages.types';
import { fetchRoomSubscriptions } from '../subscriptions/subscriptions.state';

const name = 'packages';

const packagesEntityAdapter = createEntityAdapter<AsyncEntity<CompanyPackage>>({
  selectId: pkg => pkg.id,
  sortComparer: prev => {
    return prev.archived ? 1 : -1;
  },
});

export const packagesSelectors = packagesEntityAdapter.getSelectors();

export const fetchPackages = createAsyncThunkFromFetch(`${name}/fetchCompanies`, getPackages);
export const fetchPackage = createAsyncThunkFromFetch(`${name}/fetchPackage`, getPackage);
export const deletePackage = createAsyncThunkFromFetch(`${name}/deleteCompany`, deletePackageAPI);
export const createPackage = createAsyncThunkFromFetch(`${name}/createCompany`, createPackageAPI);

export const updatePackage = createAsyncThunkFromFetch(`${name}/updateCompany`, updatePackageAPI);

export const activatePackage = createAsyncThunkFromFetch(`${name}/activatePackage`, postPackageActivate);

const packagesSlice = createSlice({
  name,
  initialState: packagesEntityAdapter.getInitialState<{
    isPending: boolean;
    hasError: boolean;
    companySelector: Record<string, string[]>;
  }>({
    isPending: false,
    hasError: false,
    companySelector: {},
  }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchPackages.pending, state => {
      state.isPending = true;
      state.hasError = false;
    });
    builder.addCase(fetchPackages.fulfilled, (state, { payload, meta }) => {
      const { companyId } = meta.arg;
      packagesEntityAdapter.upsertMany(state, payload);
      state.companySelector[companyId] = payload.map(p => p.id);
      state.isPending = false;
      state.hasError = false;
    });
    builder.addCase(fetchPackages.rejected, state => {
      state.isPending = false;
      state.hasError = true;
    });
    builder.addCase(deletePackage.pending, (state, { meta }) => {
      packagesEntityAdapter.updateOne(state, {
        id: meta.arg.packageId,
        changes: { isPending: true },
      });
    });
    builder.addCase(deletePackage.fulfilled, (state, { meta, payload }) => {
      packagesEntityAdapter.updateOne(state, {
        id: meta.arg.packageId,
        changes: { ...payload, isPending: false },
      });
    });
    builder.addCase(deletePackage.rejected, (state, { meta }) => {
      packagesEntityAdapter.updateOne(state, {
        id: meta.arg.packageId,
        changes: { isPending: false, hasError: true },
      });
    });
    builder.addCase(createPackage.fulfilled, (state, { payload }) => {
      packagesEntityAdapter.addOne(state, payload);
    });
    builder.addCase(fetchPackage.fulfilled, (state, { payload }) => {
      packagesEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(updatePackage.fulfilled, (state, { payload }) => {
      packagesEntityAdapter.updateOne(state, {
        id: payload.id,
        changes: payload,
      });
    });
    builder.addCase(activatePackage.fulfilled, (state, { payload }) => {
      packagesEntityAdapter.updateOne(state, {
        id: payload.id,
        changes: payload,
      });
    });
    builder.addCase(fetchRoomSubscriptions.fulfilled, (state, { payload }) => {
      packagesEntityAdapter.upsertMany(
        state,
        payload.map(p => p.package)
      );
    });
  },
});

const { reducer } = packagesSlice;

export default reducer;
